import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Chip,
    Grid,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GET } from "../../../Utilities/BaseService";
import {
    filterSelectionMessage,
    hasValue,
    noDataMessage,
} from "../../../Utilities/constants";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";

/* ================= TYPES ================= */

type Filter = {
    search: string;
    district: number | "" | "all";
    powerstation: number | "";
};

const initialFilter: Filter = {
    search: "",
    district: "",
    powerstation: "",
};

const initialClearAll = {
    district: false,
    powerstation: false,
    search: false,
};

/* ================= COMPONENT ================= */

const Villages = () => {
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const [rowsCount, setRowsCount] = useState(0);
    const [_data,_setData] = useState<any>([]);
    const [pagination, setPagination] = useState({ page: 0, limit: 100 });
    const [filter, setFilter] = useState<any>(initialFilter);
    const [intermediateFilter, setIntermediateFilter] =
        useState<Filter>(initialFilter);
    const [changeCount, setChangeCount] = useState(0);
    const [feedback, setFeedback] = useState("");

    const [clearAll, setClearAll] = useState(initialClearAll);

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters
        setIntermediateFilter(initialFilter);

        // reset applied filters
        setFilter(initialFilter);

        // notify searchable inputs
        setClearAll({
            district: true,
            powerstation: true,
            search: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // reset flags after one render
        setTimeout(() => setClearAll(initialClearAll), 0);
    };

    /* ================= API ================= */

    const getVillages = () => {
        return GET("/region/village", {
            page: pagination.page + 1,
            limit: pagination.limit,
            search: filter.search,
            district:
                filter.district === "all" || filter.district === ""
                    ? undefined
                    : filter.district,
            powerstation: filter.powerstation || undefined,
        });
    };


   
    const { data, isLoading, isError, isSuccess } = useQuery(
        ["villages", pagination.page, pagination.limit, filter],
        getVillages,
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    /* ================= APPLY FILTER ================= */

    useEffect(() => {
        if (changeCount) {
            setFilter(intermediateFilter);
        }
    }, [changeCount]);


  

    /* ================= UI ================= */


    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2, mb: "1rem" }}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} md="auto">
                        <Typography color="GrayText" variant="h5">
                            Villages
                        </Typography>
                    </Grid>

                    {/* ADD BUTTON */}
                    <Grid
                        item
                        xs={12}
                        md
                        container
                        spacing={1.5}
                        justifyContent="flex-end"
                    >
                        <Grid item xs={12} md="auto">
                            <MultiPermissionAuthorize ops={["CREATE VILLAGE"]}>
                                <Link
                                    to="/regions/villages/add"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                        sx={{ minWidth: "20ch" }}
                                        fullWidth
                                    >
                                        add village
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>

                    {/* FILTERS */}
                    <Grid item container xs={12} spacing={1.5}>
                        <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                            <Grid item xs={12} md={4}>
                                <UpdatedSearchableInput
                                    api="/region/district"
                                    label="District"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    clearAll={clearAll.district}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                            <Grid item xs={12} md={4}>
                                <UpdatedSearchableInput
                                    api="/powerstation"
                                    label="Powerstation"
                                    filter={intermediateFilter}
                                    setFilter={setIntermediateFilter}
                                    dep={intermediateFilter.district}
                                    params={{
                                        district: intermediateFilter.district,
                                    }}
                                    clearAll={clearAll.powerstation}
                                />
                            </Grid>
                        </MultiPermissionAuthorize>

                        <Grid item xs={12} md={4}>
                            <SearchBar
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                clear={clearAll.search}
                            />
                        </Grid>

                        {/* SEARCH */}
                        <Grid item xs={12} md={4}>
                            <Button
                                color="secondary"
                                variant="outlined"
                                startIcon={<FilterAltOutlined />}
                                onClick={() =>
                                    setChangeCount((prev) => prev + 1)
                                }
                                sx={{ minWidth: "20ch", height: "100%" }}
                                fullWidth
                            >
                                Search
                            </Button>
                        </Grid>

                        {/* CLEAR ALL */}
                        <Grid item xs={12} md={4}>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<FilterAltOffOutlined />}
                                onClick={handleClearAll}
                                sx={{ minWidth: "20ch", height: "100%" }}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Card>

            {/* INFO MESSAGE */}
            {(!changeCount || !data?.data.rows.length || isError) && (
                <Alert severity="info">
                    {!data?.data.rows.length && isSuccess
                        ? noDataMessage
                        : !hasValue(filter) && !changeCount
                        ? filterSelectionMessage
                        : ""}
                </Alert>
            )}

            {/* TABLE */}
            <Box sx={{ height: "70%" }}>
                <BaseTable
                    headers={{
                        id: "ID",
                        name: "Name",
                        districtName: "District",
                        powerstations: "Powerstation",
                    }}
                    data={data?.data.rows.map((row: any) => ({
                        ...row,
                        districtName: row.district.name,
                        powerstations: (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {row?.villagePowerstation?.map((item: any) => (
                                    <Chip
                                        key={
                                            item?.powerstation?.id ??
                                            item?.powerstation?.name
                                        }
                                        label={item?.powerstation?.name}
                                        size="small"
                                    />
                                ))}
                            </Stack>
                        ),
                    }))}
                    delEndPoint="/region/village"
                    defaultSelectedHeaders={[
                        "id",
                        "name",
                        "districtName",
                        "powerstations",
                    ]}
                    feedback={feedback}
                    setFeedback={setFeedback}
                    rowsCount={rowsCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    isLoading={isLoading}
                    load={Boolean(changeCount)}
                    permissions={{
                        edit: ["UPDATE VILLAGE"],
                        delete: ["DELETE VILLAGE"],
                    }}
                    queryKey={[
                        "villages",
                        pagination.page,
                        pagination.limit,
                        filter,
                    ]}
                    reportName="Villages__List"
                    reportDetails={{
                        district: filter.district ? filter.district : undefined,
                        powerstation: filter.powerstation
                            ? filter.powerstation
                            : undefined,
                        village: filter.village ? filter.village : undefined,
                        hamlet: filter.hamlet ? filter.hamlet : undefined,
                    }}
                />
            </Box>
        </Box>
    );
};

export default Villages;
